import { ComponentProps } from "@/baseComponents";
import { Button } from "@/components/atoms/button";
import { ButtonProps } from "@/components/atoms/button/Button";
import { CustomGrid } from "@/components/layout/grid";
import { FactCard } from "@/components/molecules/factCard";
import { FactCardProps } from "@/components/molecules/factCard/FactCard";
import { TrackEvent } from "@/utils/trackingHelpers";
import { chakra, Flex, GridItem, Heading } from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";
import { Swiper, SwiperSlide } from "swiper/react";
import styles from "./FactCardSliderBlock.module.scss";

import { ButtonVariants } from "@/types/enums";
import "swiper/css";

export interface FactCardSliderBlockProps extends ComponentProps {
  title: string;
  cards: FactCardProps[];
  readMoreButton?: ButtonProps;
  cardBackgroundColor?: string;
}

/**
 * Represents a Fact card slider block component
 * @returns factCardSliderBlock
 */
export default chakra(FactCardSliderBlock);
function FactCardSliderBlock({
  title,
  cards,
  readMoreButton,
  cardBackgroundColor,
  theme = "brand",
}: FactCardSliderBlockProps) {
  const ChakraSwiper = chakra(Swiper);

  return (
    <CustomGrid templateColumns="repeat(12, 1fr)" px={0}>
      {title && (
        <GridItem colSpan={12}>
          <Heading
            as={"h2"}
            width="100%"
            textAlign="center"
            fontFamily="Cooper"
            fontSize="3rem"
            fontWeight={400}
            color={`${theme}.aubergine`}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(title.replace(/<\/p>/g, "</br>"), {
                ALLOWED_TAGS: ["strong", "br"],
              }),
            }}
            sx={{
              strong: {
                fontWeight: 400,
                color: "brand.cyan",
                fontFamily: "Lausanne, sans-serif",
              },
            }}
          />
        </GridItem>
      )}
      <GridItem colSpan={12}>
        <ChakraSwiper
          onSlideChange={(swiper) => {
            const currentSlide = swiper.activeIndex;
            const currentCardTitle = cards[currentSlide]?.title || "";

            TrackEvent("content_interaction", {
              interaction_type: "slide_change",
              block_name: "FactCardSliderBlock",
              slide_number: currentSlide,
              card_title: currentCardTitle,
            });
          }}
          spaceBetween={0} // No space between slides, we handle this in the slide self
          centerInsufficientSlides={true}
          slideToClickedSlide={true}
          centeredSlides={false}
          grabCursor={true}
          breakpoints={{
            320: {
              slidesPerView: 1.15,
            },
            375: {
              slidesPerView: 1.3,
            },
            425: {
              slidesPerView: 1.4,
            },
            768: {
              slidesPerView: 1.9,
            },
            1024: {
              slidesPerView: 2.2,
            },
            1440: {
              slidesPerView: 3.1,
            },
            1640: {
              slidesPerView: 3.5,
            },
            1920: {
              slidesPerView: 4.2,
            },
            2560: {
              slidesPerView: 5.2,
            },
          }}
          paddingLeft={{
            base: "0.5rem !important",
            sm: "1rem !important",
            lg: "2.5rem !important",
          }}
          paddingRight={{
            base: "0.5rem !important",
            sm: "1rem !important",
            lg: "2.5rem !important",
          }}
        >
          {cards?.map((card, index) => (
            <SwiperSlide className={`${styles["swiper-slide"]}`} key={index}>
              <FactCard
                title={card.title}
                subTitle={card.subTitle}
                cardBackgroundColor={cardBackgroundColor}
                value={card.value}
                marginRight={
                  index === cards.length - 1
                    ? "0"
                    : { base: "2.5rem", md: "3.125rem", lg: "5rem" }
                }
              />
            </SwiperSlide>
          ))}
        </ChakraSwiper>
      </GridItem>
      {readMoreButton && (
        <GridItem colSpan={12}>
          <Flex w={"100%"} justifyContent="center">
            <Button
              buttonVariant={
                readMoreButton?.buttonVariant || ButtonVariants.Secondary
              }
              contentInteractionEventProps={{
                interactionType: "button_click",
                blockName: "FactCardSliderBlock",
                clickText: readMoreButton?.text || "",
              }}
              {...readMoreButton}
            />
          </Flex>
        </GridItem>
      )}
    </CustomGrid>
  );
}
